import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import type { Label, Task } from '../../../lib/tasksApi';
import type { TaskStatusDef } from '../../../lib/taskStatusesApi';
import { useTaskStatuses } from '../../../hooks/useTaskStatuses';
import { useCreateTask } from '../../../hooks/useTasks';
import { StatusIcon, WarningTriangleIcon } from './taskVisuals';
import { TaskRow } from './TaskRow';
import { DragContext, type DragApi, type DragState, type DropPosition } from './dragContext';
import { useOptimisticTaskUpdate } from './useOptimisticTaskUpdate';
import { useToast } from './Toast';
import { dueUrgency } from './dueDate';
import { ProjectSelect } from '../ProjectSelect';
import { useI18n } from '../../../contexts/I18nContext';
import { useConfirm } from '../../ui/ConfirmProvider';
import { translate } from '../../../lib/i18n';

/**
 * CLE-174 / CLE-179 — pixel-perfect Linear-clone task list with drag-and-drop.
 *
 * Tasks are grouped into collapsible status sections. Statuses are configurable
 * DB rows (CLE-DASH): the group order comes from `useTaskStatuses().listGroupOrder`
 * (STARTED → UNSTARTED → BACKLOG → COMPLETED → CANCELLED) and groups are keyed
 * by status id. Each group exposes a quick-add row, and rows can be dragged
 * between groups to change status (or reordered within a group). Multi-select
 * drags move every checked row at once. Moves PATCH the task status
 * optimistically and surface a toast.
 */

const COLLAPSE_KEY = 'cle197:collapsedStatusGroups:v2';

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

interface LinearTaskListProps {
  tasks: Task[];
  selected: Set<string>;
  onToggleSelect: (id: string, shift: boolean) => void;
  onOpen: (id: string) => void;
  onLabelClick?: (label: Label) => void;
  /** When set, quick-add creates tasks in this project. */
  projectId?: string | null;
  /** Show the parent project chip on each row (cross-project lists only). */
  showProjectName?: boolean;
}

export function LinearTaskList({
  tasks,
  selected,
  onToggleSelect,
  onOpen,
  onLabelClick,
  projectId,
  showProjectName,
}: LinearTaskListProps) {
  const { t } = useI18n();
  const { listGroupOrder, byId: statusById } = useTaskStatuses();
  // Collapse state is keyed by status id.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed());
  const [quickAddFor, setQuickAddFor] = useState<string | null>(null);
  const update = useOptimisticTaskUpdate();
  const toast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...collapsed]));
    } catch {
      // Non-fatal: persistence is a convenience only.
    }
  }, [collapsed]);

  // Local per-group ordering so a within-group reorder shows instantly. Keyed
  // by status id → task ids; re-synced whenever the upstream task set changes.
  const [order, setOrder] = useState<Record<string, string[]>>({});

  const grouped = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const s of listGroupOrder) map.set(s.id, []);
    for (const t of tasks) {
      if (!map.has(t.statusId)) map.set(t.statusId, []);
      map.get(t.statusId)!.push(t);
    }
    // Apply any local ordering override for the group.
    for (const [statusId, list] of map) {
      const ord = order[statusId];
      if (!ord) continue;
      const byId = new Map(list.map((t) => [t.id, t]));
      const sorted: Task[] = [];
      for (const id of ord) {
        const t = byId.get(id);
        if (t) {
          sorted.push(t);
          byId.delete(id);
        }
      }
      // Any tasks not in the override (newly added) keep their natural order.
      for (const t of list) if (byId.has(t.id)) sorted.push(t);
      map.set(statusId, sorted);
    }
    return map;
  }, [tasks, order, listGroupOrder]);

  // --- Drag-and-drop state -------------------------------------------------
  const [drag, setDrag] = useState<DragState>({
    active: false,
    draggingIds: new Set(),
    drop: null,
    sourceStatus: null,
  });
  // Mirror in a ref so async drop handlers read the latest target.
  const dragRef = useRef(drag);
  dragRef.current = drag;

  const canDropOn = useCallback((statusId: string): boolean => {
    return statusId !== undefined;
  }, []);

  const onRowDragStart = useCallback(
    (taskId: string, statusId: string, e: React.DragEvent) => {
      // Multi-drag: if the grabbed row is part of the selection, move all
      // selected rows; otherwise just the one.
      const ids = selected.has(taskId) && selected.size > 1 ? new Set(selected) : new Set([taskId]);
      try {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', taskId);
        // A custom drag image showing the moved count for multi-drag.
        if (ids.size > 1) {
          const ghost = buildMultiGhost(ids.size);
          document.body.appendChild(ghost);
          e.dataTransfer.setDragImage(ghost, 12, 12);
          window.setTimeout(() => ghost.remove(), 0);
        }
      } catch {
        // Some environments restrict dataTransfer; drag still works via state.
      }
      setDrag({ active: true, draggingIds: ids, drop: null, sourceStatus: statusId });
    },
    [selected],
  );

  const onRowDragEnd = useCallback(() => {
    setDrag({ active: false, draggingIds: new Set(), drop: null, sourceStatus: null });
  }, []);

  const onRowDragOver = useCallback(
    (taskId: string, statusId: string, e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const after = e.clientY - rect.top > rect.height / 2;
      const list = grouped.get(statusId) ?? [];
      const idx = list.findIndex((t) => t.id === taskId);
      const beforeId = after ? (list[idx + 1]?.id ?? null) : taskId;
      setDrag((d) => updateDrop(d, { status: statusId, beforeId }));
    },
    [grouped],
  );

  const onGroupDragOver = useCallback((statusId: string, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Targeting the header/empty area drops at the end of the group.
    setDrag((d) => updateDrop(d, { status: statusId, beforeId: null }));
  }, []);

  const onDrop = useCallback(
    async (statusId: string) => {
      const current = dragRef.current;
      const target = current.drop ?? { status: statusId, beforeId: null };
      const ids = [...current.draggingIds];
      setDrag({ active: false, draggingIds: new Set(), drop: null, sourceStatus: null });
      if (ids.length === 0) return;

      const targetStatus = statusById[target.status];
      if (!targetStatus) return;
      const movingToNewStatus = current.sourceStatus !== target.status;

      // Completed drops are optional/with confirm (CLE-179 restriction).
      if (targetStatus.category === 'COMPLETED' && movingToNewStatus) {
        const ok = await confirm({
          message:
            ids.length > 1
              ? `Marchezi ${ids.length} sarcini ca Finalizate?`
              : 'Marchezi această sarcină ca Finalizată?',
        });
        if (!ok) return;
      }

      // Reorder locally within the target group for instant feedback.
      applyLocalMove(setOrder, grouped, ids, target);

      if (movingToNewStatus) {
        for (const id of ids) {
          const t = tasks.find((x) => x.id === id);
          if (!t || t.statusId === target.status) continue;
          update.mutate({
            id,
            input: { statusId: targetStatus.id },
            patch: (task) => ({
              ...task,
              status: targetStatus.slug,
              statusId: targetStatus.id,
              statusLabel: targetStatus.name,
              statusColor: targetStatus.color,
              statusCategory: targetStatus.category,
              statusOrder: targetStatus.order,
            }),
          });
        }
        const label = targetStatus.name;
        if (ids.length > 1) {
          toast.show(translate('tasks.list.movedMany', undefined, { count: ids.length, label }));
        } else {
          const moved = tasks.find((x) => x.id === ids[0]);
          const ref = moved?.identifier ?? translate('tasks.list.taskFallback');
          toast.show(translate('tasks.list.movedOne', undefined, { ref, label }));
        }
      }
    },
    [grouped, tasks, update, toast, statusById, confirm],
  );

  const dragApi: DragApi = useMemo(
    () => ({
      state: drag,
      onRowDragStart,
      onRowDragEnd,
      onRowDragOver,
      onGroupDragOver,
      onDrop,
      canDropOn,
    }),
    [drag, onRowDragStart, onRowDragEnd, onRowDragOver, onGroupDragOver, onDrop, canDropOn],
  );

  function toggleCollapse(statusId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(statusId)) next.delete(statusId);
      else next.add(statusId);
      return next;
    });
  }

  // Only render Cancelled groups when they actually have tasks (keeps the
  // active flow clean).
  const visibleStatuses = listGroupOrder.filter(
    (s) => s.category !== 'CANCELLED' || (grouped.get(s.id)?.length ?? 0) > 0,
  );

  return (
    <DragContext.Provider value={dragApi}>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-card">
        {visibleStatuses.map((status) => {
          const groupTasks = grouped.get(status.id) ?? [];
          const isCollapsed = collapsed.has(status.id);
          const hasOverdue = groupTasks.some((t) => dueUrgency(t.dueDate) === 'overdue');
          const isDropTarget = drag.active && drag.drop?.status === status.id;
          return (
            <section
              key={status.id}
              onDragOver={(e) => drag.active && onGroupDragOver(status.id, e)}
              onDrop={(e) => {
                if (!drag.active) return;
                e.preventDefault();
                onDrop(status.id);
              }}
            >
              <GroupHeader
                status={status}
                count={groupTasks.length}
                canQuickAdd
                showWarning={hasOverdue}
                isDropTarget={isDropTarget}
                onToggle={() => toggleCollapse(status.id)}
                collapsed={isCollapsed}
                onQuickAdd={() => {
                  if (isCollapsed) toggleCollapse(status.id);
                  setQuickAddFor(status.id);
                }}
              />

              {!isCollapsed && (
                <div role="rowgroup">
                  {quickAddFor === status.id && (
                    <QuickAddRow
                      status={status}
                      projectId={projectId ?? null}
                      onClose={() => setQuickAddFor(null)}
                    />
                  )}
                  {groupTasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      selected={selected.has(task.id)}
                      onToggleSelect={onToggleSelect}
                      onOpen={onOpen}
                      onLabelClick={onLabelClick}
                      showProjectName={showProjectName}
                    />
                  ))}
                  {groupTasks.length === 0 && quickAddFor !== status.id && (
                    <p
                      className={`px-4 py-2 text-xs transition-colors ${
                        isDropTarget ? 'text-brand-600' : 'text-gray-400'
                      }`}
                    >
                      {isDropTarget
                        ? t('tasks.list.dropToMove', { label: status.name })
                        : t('tasks.list.noTask')}
                    </p>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </DragContext.Provider>
  );
}

// --- Drag helpers ----------------------------------------------------------

/** Only update drop state when the target actually changed (avoids churn). */
function updateDrop(d: DragState, drop: DropPosition): DragState {
  if (d.drop && d.drop.status === drop.status && d.drop.beforeId === drop.beforeId) return d;
  return { ...d, drop };
}

/** Apply a move to the local per-group ordering for instant feedback. */
function applyLocalMove(
  setOrder: React.Dispatch<React.SetStateAction<Record<string, string[]>>>,
  grouped: Map<string, Task[]>,
  ids: string[],
  target: DropPosition,
) {
  setOrder((prev) => {
    const next = { ...prev };
    const moving = new Set(ids);
    // Remove moving ids from every group's current order.
    for (const statusId of grouped.keys()) {
      const base = next[statusId] ?? grouped.get(statusId)?.map((t) => t.id) ?? [];
      next[statusId] = base.filter((id) => !moving.has(id));
    }
    // Insert into the target group at the requested position.
    const targetBase = next[target.status] ?? [];
    const insertAt =
      target.beforeId === null
        ? targetBase.length
        : Math.max(0, targetBase.indexOf(target.beforeId));
    next[target.status] = [...targetBase.slice(0, insertAt), ...ids, ...targetBase.slice(insertAt)];
    return next;
  });
}

/** Build a stacked-card ghost element that reads "N tasks" for multi-drag. */
function buildMultiGhost(count: number): HTMLElement {
  const el = document.createElement('div');
  el.textContent = translate('tasks.list.ghostCount', undefined, { count });
  el.style.cssText = [
    'position:absolute',
    'top:-1000px',
    'left:-1000px',
    'padding:6px 12px',
    'font:500 13px ui-sans-serif,system-ui,sans-serif',
    'color:#e5e5e5',
    'background:#232328',
    'border:1px solid #3f3f46',
    'border-radius:8px',
    'box-shadow:6px 6px 0 -2px #1a1a1e, 6px 6px 0 -1px #3f3f46, 0 8px 24px rgba(0,0,0,.4)',
  ].join(';');
  return el;
}

function GroupHeader({
  status,
  count,
  collapsed,
  canQuickAdd,
  showWarning,
  isDropTarget,
  onToggle,
  onQuickAdd,
}: {
  status: TaskStatusDef;
  count: number;
  collapsed: boolean;
  canQuickAdd: boolean;
  showWarning: boolean;
  isDropTarget: boolean;
  onToggle: () => void;
  onQuickAdd: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      className={`relative flex h-8 items-center border-b border-gray-200 text-[13px] transition-colors ${
        isDropTarget ? 'bg-brand-50 ring-1 ring-inset ring-brand-600' : 'bg-card'
      }`}
      style={{ paddingLeft: 20 }}
    >
      {isDropTarget ? (
        <span className="flex items-center gap-2 font-medium text-brand-600">
          <ChevronRight size={16} className="rotate-90" />
          {t('tasks.list.dropToMove', { label: status.name })}
        </span>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center"
          aria-expanded={!collapsed}
          aria-label={`${collapsed ? t('tasks.list.expand') : t('tasks.list.collapse')} ${status.name}`}
        >
          <ChevronRight
            size={16}
            className={`text-gray-400 transition-transform ${collapsed ? '' : 'rotate-90'}`}
            style={{ marginRight: 4 }}
          />
          <StatusIcon category={status.category} color={status.color} size={16} />
          <span className="font-semibold text-gray-800" style={{ marginLeft: 6 }}>
            {status.name}
          </span>
          {showWarning && (
            <span style={{ marginLeft: 8 }}>
              <WarningTriangleIcon size={12} />
            </span>
          )}
          <span className="font-normal text-gray-500" style={{ marginLeft: 4 }}>
            {count}
          </span>
        </button>
      )}

      {canQuickAdd && !isDropTarget && (
        <button
          type="button"
          onClick={onQuickAdd}
          className="absolute right-3 flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-200 hover:text-gray-700"
          aria-label={t('tasks.list.addTo', { label: status.name })}
          title={t('tasks.addToColumn')}
        >
          <Plus size={15} />
        </button>
      )}
    </div>
  );
}

function QuickAddRow({
  status,
  projectId,
  onClose,
}: {
  status: TaskStatusDef;
  // CLE-199: null when the list is cross-project — the row shows an inline
  // project picker so a task can still be created without a project filter.
  projectId: string | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [pickedProjectId, setPickedProjectId] = useState('');
  const create = useCreateTask();

  const effectiveProjectId = projectId ?? pickedProjectId;

  function submit() {
    const trimmed = title.trim();
    // Need both a project and a title; keep the row open until the project is
    // chosen so the user isn't silently dropped.
    if (!effectiveProjectId) return;
    if (!trimmed) {
      onClose();
      return;
    }
    create.mutate(
      { title: trimmed, statusId: status.id, projectId: effectiveProjectId },
      {
        onSuccess: () => setTitle(''),
      },
    );
  }

  return (
    <div className="flex h-9 items-center gap-2 border-b border-gray-200 bg-gray-200 px-4">
      <StatusIcon category={status.category} color={status.color} size={14} />
      {!projectId && (
        <ProjectSelect
          value={pickedProjectId}
          onChange={setPickedProjectId}
          dark
          width={260}
          placeholder={t('tasks.quickAdd.projectShort')}
        />
      )}
      <input
        autoFocus={Boolean(projectId)}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
        onBlur={() => {
          // Keep the row open while a create is in flight or while the user
          // still has to choose a project; otherwise close empty rows.
          if (!title.trim() && !create.isPending && effectiveProjectId) onClose();
        }}
        placeholder={
          effectiveProjectId ? t('tasks.quickAdd.newTitle') : t('tasks.quickAdd.pickProjectFirst')
        }
        disabled={create.isPending}
        className="min-w-0 flex-1 bg-transparent text-[13px] text-gray-800 placeholder:text-gray-400 focus:outline-none disabled:opacity-60"
      />
      {create.isError && <span className="text-xs text-red-400">{t('tasks.quickAdd.failed')}</span>}
    </div>
  );
}
